import React from 'react';
import PropTypes from 'prop-types';
import requireScript from '../../../utils/requireScript';
import {customers, paymentProfiles, users, modals} from "../../../ducks";
import {dependentDuck, duckify, filterDuck, singleDuck} from "../../../utils/duckify";
import Placeholder from "../../Placeholder";
import FA from '../../../utils/FA';
import NachaMessage from '../NachaMessage';
import {paymentTypes} from "components/payments/paymentInputs/PaymentSelector";
import ConvenienceFeeCheckbox from "components/payments/paymentInputs/ConvenienceFeeCheckbox";
import translatePaymentDetails from "components/payments/translatePaymentDetails";
import PendingAchInput from "components/payments/paymentInputs/PendingAchInput";
import PaymentDetailsInput from "components/payments/detailInputs/PaymentDetailsInput";
import {paymentMethodDisplayName, submitButtonText} from "./PaymentSelector";
import { paymentsEnabled } from 'components/payments/stripe/stripeUtils';

const ERROR_WHITELIST = [
  "You cannot take that action while impersonating a parent account."
]

class ManualAchInput extends React.Component {
  static propTypes = {
    order: PropTypes.shape({
      entity_id: PropTypes.string.isRequired,
      entity_class: PropTypes.string.isRequired
    }).isRequired,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Amount in dollars

    buttonText: PropTypes.func,
    submittingText: PropTypes.func
  };

  static defaultProps = {
    amount: 0,
    buttonText: submitButtonText,
    submittingText: (customer, method) => `Submitting ${paymentMethodDisplayName(customer, method)}...`,
    profileCreated: null
  };


  constructor(){
    super();

    this.state = {
      disabled: false,
      submitting: false,
      savePaymentProfile: true,
      accountNumber: null,
      routingNumber: null,
      error: false
    };

    this.loadStripe();
    this.handleSubmit = this.handleSubmit.bind(this);

  }

  loadStripe() {
    return requireScript('https://js.stripe.com/v3/?advancedFraudSignals=false', {
      unless: global.Stripe
    });
  }

  getStripeToken() {
    const {
      paymentDetails,
      customer
    } = this.props;

    const {
      accountNumber,
      routingNumber
    } = this.state;

    if(global.PAYMENTS_TEST_MODE) {
      const {customer} = this.props;

      return Promise.resolve({
        token: {
          id: customer.ach_payment_info.dummy_pending_bank_token || 'btok_stripe_1'
        }
      });
    }
    else {
      return this.loadStripe().then(() => {
        const stripe = global.Stripe(customer.stripe_api_key);
        return stripe.createToken('bank_account', {
          country: customer.country,
          currency: customer.currency.toLowerCase(),
          routing_number: routingNumber,
          account_number: accountNumber,
          account_holder_name: paymentDetails.name,
          account_holder_type: 'individual',
        });
      });
    }
  }

  overwriteWarning() {
    const {
      customer,
      dispatch,
      profile
    } = this.props;

    // StripeGateway supports only a single payment profile; if we have an existing profile, display the warning
    if (customer.cc_payment_gateway === 'StripeGateway' && profile.length > 0) {
      dispatch(
        modals.actions.show('VerificationModal', {
          message: (
            <div>
              <p>Our records show you have an existing Payment Profile.</p>
              <p>
                Saving this information will overwrite the existing profile
                and begin the micro deposit verification process.
              </p>
              <p>Would you like to proceed?</p>
            </div>
          ),
          onConfirm: this.handleSubmit.bind(this)
        })
      );
    } else {
      this.handleSubmit();
    }
  }

  handleSubmit() {
    const {
      manualAchSubmit,
      paymentDetails,
      beforeChange
    } = this.props;

    this.setState({
      submitting: true
    });

    return this.getStripeToken()
      .then((result) => manualAchSubmit({
        token: result.token.id,
        paymentDetails
      })
      .then((profileCreated) => {
        beforeChange(paymentTypes.PAYMENT_PROFILE, profileCreated.id)
          .then(() => this.setState({ profileCreated }));
      }))
      .catch(this.handleError.bind(this));
  }

  handleError(error) {
    const errorMessage = error && error.message;

    const message = ERROR_WHITELIST.includes(errorMessage) ? errorMessage :
      'We were unable to verify your account information.  Please check the routing and account number and try again.'

    this.setState({
      error: message,
      submitting: false,
      disabled: false
    });
  }


  convenienceFeeConfirmationRequired(){
    const {
      customer: {
        payment_config: {
          payment_methods: {
            [paymentTypes.ACH]: {
              convenience_fee: {
                show_confirmation_message = false
              } = {}
            } = {}
          } = {}
        } = {}
      },
      convenienceFee
    } = this.props;

    return show_confirmation_message && (convenienceFee != 0);
  }

  update(field) {
    return (event) => {
      this.setState({[field]: event.target.value});
    }
  }

  isValid() {
    const {
      paymentDetails
    } = this.props;

    const {
      routingNumber,
      accountNumber
    } = this.state;

    return paymentDetails.valid && routingNumber && accountNumber;
  }


  render(){
    const {
      customer,
      amount,
      allDucksReady
    } = this.props;

    const buttonText = this.props.buttonText(customer, paymentTypes.ACH);
    const submittingText = this.props.submittingText(customer, paymentTypes.ACH);

    const {
      routingNumber,
      accountNumber,
      submitting,
      convenienceFeeAccepted,
      profileCreated,
      error
    } = this.state;

    const disabled = this.props.disabled || this.state.disabled;

    const convenienceFeeOk = convenienceFeeAccepted || !this.convenienceFeeConfirmationRequired();

    if (!paymentsEnabled(customer.ach_payment_gateway, customer.ach_payment_info)) {
      return <div className={'alert alert-danger'}>
        <FA name={'warning'} nbsp/>
        Electronic Check payments have not been configured.  Please contact the system administrator.
      </div>;
    }

    if (profileCreated) {
      return <PendingAchInput paymentProfile={profileCreated} {...this.props}/>;
    }

    return <Placeholder.loading ready={allDucksReady} content={()=>
      <div className={'payment-details'}>
        {!!error && <div className={'alert alert-danger'}>
          {error}
        </div>}

        <div className={'alert alert-info'}>
          <p>
            {customer.long_name} provides an alternative and secure way to process {paymentMethodDisplayName(customer, paymentTypes.ACH)} withdrawals by linking to banks using user’s account and routing numbers.
          </p>
          <ul>
            <li>Two micro deposits will be made to your account.</li>
            <li>The transfers can take 1-3 business days to appear on your account.</li>
            <li>Once those deposits have been made, log back into your portal to verify those deposits.</li>
          </ul>
        </div>

        <fieldset className={'payment-ach-input ach-manual'}>
          <PaymentDetailsInput onChange={this.props.paymentDetailsChanged} customer={customer} disabled={submitting} />

          <label className={`payment-ach-field`}>
            <span className={'payment-input-label'}>
              Routing Number:
            </span>

            <input type={'text'}
                   className={'payment-input-field'}
                   id={'ach_routing_number'}
                   name={'ach_routing_number'}
                   onChange={this.update('routingNumber')}
                   placeholder={'000000000'}
                   value={routingNumber}
                   disabled={submitting}
            />
          </label>

          <label className={`payment-ach-field`}>
            <span className={'payment-input-label'}>
              Account Number:
            </span>

            <input type={'text'}
                   className={'payment-input-field'}
                   id={'ach_account_number'}
                   name={'ach_account_number'}
                   onChange={this.update('accountNumber')}
                   placeholder={'000000000'}
                   value={accountNumber}
                   disabled={submitting}
            />
          </label>
        </fieldset>

        {this.convenienceFeeConfirmationRequired() && (
          <ConvenienceFeeCheckbox
            customer={customer}
            checked={convenienceFeeAccepted}
            paymentMethod={paymentTypes.ACH}
            onChange={(...params) => {
              this.setState({ convenienceFeeAccepted: !this.state.convenienceFeeAccepted });
            }}
            disabled={submitting}
          />
        )}

        {submitting ?
          <button className={'btn btn-primary'} type={'button'} disabled>
            <FA name={'spinner'} spin nbsp/>
            {submittingText}
          </button>
          :
          <button className={'btn btn-primary'}
                  type={'button'}
                  onClick={() => this.overwriteWarning()}
                  disabled={!convenienceFeeOk || !this.isValid() || !!disabled}
          >
            {buttonText}
          </button>
        }

        <NachaMessage amount={amount}
                      buttonText={buttonText}
                      customer={customer}/>
      </div>
    }/>;
  }
}

function mapDucksToProps(ownProps) {
  const user = singleDuck(users, 'current');

  return {
    customer: singleDuck(customers, 'current'),
    profile: dependentDuck(user, ({ value }) => filterDuck(paymentProfiles, { user_id: value.id })),
    user
  };
}

function mapStateToProps(state, ownProps){
  return {};
}

function mapDispatchToProps(dispatch, ownProps){
  return {
    manualAchSubmit: ({paymentDetails, token}) => dispatch(paymentProfiles.actions.create(
        translatePaymentDetails('electronic check', paymentDetails),
        {token}
      )).then(paymentProfiles.actions.denormalizeObjects)
  };
}

export default duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)(ManualAchInput);
export const unduckified = ManualAchInput;
